import {
  createAuthFetch,
  type AuthFetch,
  type AuthFetchEnv,
} from "@axisnode/auth-client";

export type ApiClientConfig = {
  /** Absolute API origin, e.g. https://api.axisnode.site — no trailing slash required. */
  baseUrl: string;
  /** Optional auth-client env (injectables for tests / non-browser). */
  auth?: AuthFetchEnv;
  /** Override the auth fetch entirely (tests). */
  authFetch?: AuthFetch;
  /** Default headers merged into every request (Content-Type not forced — caller decides). */
  defaultHeaders?: HeadersInit;
};

export type TypedRequestOptions = Omit<RequestInit, "body"> & {
  /** JSON body — serialized; sets Content-Type application/json when body is present. */
  json?: unknown;
  /** Optional absolute override; otherwise path is resolved against baseUrl. */
  path: string;
};

export type ApiClient = {
  readonly baseUrl: string;
  readonly authFetch: AuthFetch;
  /** Low-level authenticated fetch against baseUrl + path. */
  request: (options: TypedRequestOptions) => Promise<Response>;
  /** Typed JSON helper — parses JSON on 2xx; throws ApiError otherwise. */
  requestJson: <T>(options: TypedRequestOptions) => Promise<T>;
};

export class ApiError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, body: string, message?: string) {
    super(message ?? `API request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function joinUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const base = baseUrl.replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

/**
 * Generic typed-fetch factory (APR-PP-001 §4 / AR-04).
 *
 * Owns: transport, typing helpers, serialization, auth integration.
 * Never owns: Native-System endpoint catalogs, business rules, workflow.
 */
export function createApiClient(config: ApiClientConfig): ApiClient {
  const authFetch = config.authFetch ?? createAuthFetch(config.auth ?? {});
  const baseUrl = config.baseUrl.replace(/\/+$/, "");

  async function request(options: TypedRequestOptions): Promise<Response> {
    const { path, json, headers, ...rest } = options;
    const url = joinUrl(baseUrl, path);
    const mergedHeaders = new Headers(config.defaultHeaders);
    new Headers(headers).forEach((value, key) => mergedHeaders.set(key, value));

    const init: RequestInit = {
      ...rest,
      headers: mergedHeaders,
    };
    if (json !== undefined) {
      mergedHeaders.set("Content-Type", "application/json");
      init.body = JSON.stringify(json);
    }

    return authFetch(url, init);
  }

  async function requestJson<T>(options: TypedRequestOptions): Promise<T> {
    const response = await request(options);
    const text = await response.text();
    if (!response.ok) {
      throw new ApiError(response.status, text);
    }
    if (!text) {
      return undefined as T;
    }
    return JSON.parse(text) as T;
  }

  return { baseUrl, authFetch, request, requestJson };
}
